import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GroupsApiService } from '@core/services/groups-api.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createCustomer, createCustomerSuccess, createCustomerFailure } from '../actions/create-customer.actions';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';
import { ToastService } from '@shared/services/toast.service';
import { Group } from '@shared/models/group';
import { updateLead } from '@features/leads/actions/update-lead.actions';
import { Lead } from '@shared/models/lead';

@Injectable()
export class CreateCustomerEffects {
  constructor(
    private actions$: Actions,
    private groupsApiService: GroupsApiService,
    private toastService: ToastService,
    private router: Router,
  ) {}

  createCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createCustomer),
      switchMap((action) => {
        return this.groupsApiService.create(action.group).pipe(
          switchMap((response: { data: Group }) => {
            if (action.leadId) {
              return [
                createCustomerSuccess({
                  group: response.data,
                  leadId: action.leadId,
                }),
                updateLead({
                  lead: {
                    id: action.leadId,
                    attributes: { group_id: response.data.id },
                  } as Partial<Lead>,
                }),
              ];
            } else {
              return [
                createCustomerSuccess({
                  group: response.data,
                  leadId: action.leadId,
                }),
              ];
            }
          }),
          catchError((error) => {
            return of(createCustomerFailure({ error }));
          }),
        );
      }),
    ),
  );

  createCustomerSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createCustomerSuccess),
        tap((action) => {
          this.toastService.showToast('success', 'Successfully created customer.', 3000);
          if (!action.leadId) {
            this.router.navigateByUrl('/customers/edit/' + action.group.id);
          }
        }),
      ),
    { dispatch: false },
  );

  createCustomerFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createCustomerFailure),
        tap(({ error }) => {
          const errorMessage =
            typeof error.error?.errors === 'string'
              ? error.error.errors
              : 'Failed to create customer. Please try again later.';
          this.toastService.showToast('error', errorMessage, 10000);
        }),
      ),
    { dispatch: false },
  );
}
